import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import PageContainer from "../components/layout/PageContainer";
import { useWebsite } from "../hooks/useWebsite";
import { useAccessibility } from "../hooks/useAccessibility";
import { useWordPressConnection } from "../hooks/useWordPress";
import ScanResults from "../components/scans/ScanResults";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";

const getScanStatusBadge = (status) => {
  switch (status) {
    case 'scan_completed':
      return { label: 'Scan Completed', icon: 'mdi:check-circle', cls: 'bg-green-100 text-green-700 border-green-200' }
    case 'fixes_applied':
      return { label: 'Fixes Applied', icon: 'mdi:wrench-check', cls: 'bg-teal-100 text-teal-700 border-teal-200' }
    case 'applying_fixes':
      return { label: 'Applying Fixes', icon: 'mdi:auto-fix', cls: 'bg-orange-100 text-orange-700 border-orange-200' }
    case 'in_progress':
      return { label: 'In Progress', icon: 'mdi:progress-clock', cls: 'bg-blue-100 text-blue-700 border-blue-200' }
    case 'failed':
      return { label: 'Failed', icon: 'mdi:alert-circle', cls: 'bg-red-100 text-red-700 border-red-200' }
    default:
      return null
  }
}

const getImpactColor = (impact) => {
  switch (impact?.toLowerCase()) {
    case "critical":
      return "bg-red-100 text-red-700 border-red-200";
    case "serious":
      return "bg-orange-100 text-orange-700 border-orange-200";
    case "moderate":
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    case "minor":
      return "bg-blue-100 text-blue-700 border-blue-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
};

const getScoreColor = (score) => {
  if (score >= 90) return "text-green-600 bg-green-50 border-green-200";
  if (score >= 70) return "text-yellow-600 bg-yellow-50 border-yellow-200";
  return "text-red-600 bg-red-50 border-red-200";
};

const calcScore = (violations = []) => {
  let penalty = 0;
  violations.forEach((v) => {
    const impact = v.impact?.toLowerCase();
    const nodeCount = v.nodes?.length || 1;
    if (impact === "critical") penalty += 15 * nodeCount;
    else if (impact === "serious") penalty += 10 * nodeCount;
    else if (impact === "moderate") penalty += 5 * nodeCount;
    else penalty += 2 * nodeCount;
  });
  return Math.max(0, 100 - penalty);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const ScanListItem = ({ scan, isSelected, onSelect }) => {
  const summary = scan.summary ?? {};
  const violationCount = summary.totalViolations ?? scan.violations?.length ?? 0;
  const score = summary.complianceScore ?? calcScore(scan.violations ?? []);
  const criticalCount = summary.violationsByImpact?.critical ?? 0;
  const seriousCount = summary.violationsByImpact?.serious ?? 0;

  return (
    <button
      onClick={() => onSelect(scan)}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        isSelected
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <p className="text-xs font-medium text-gray-800 break-all leading-snug line-clamp-2">
          {scan.url}
        </p>
        <span
          className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded border ${getScoreColor(score)}`}
        >
          {score}%
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-gray-400 flex items-center gap-1">
          <Icon icon="mdi:calendar-clock" className="text-xs" />
          {formatDate(scan.createdAt)}
        </span>
        {scan.wcagLevel && (
          <span className="text-[10px] font-bold text-primary uppercase">
            {scan.wcagLevel}
          </span>
        )}
        {(() => {
          const badge = getScanStatusBadge(scan.scanStatus)
          return badge ? (
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border flex items-center gap-0.5 ${badge.cls}`}>
              <Icon icon={badge.icon} className="text-[10px]" />
              {badge.label}
            </span>
          ) : null
        })()}
      </div>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <span
          className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${violationCount > 0 ? "bg-red-50 text-red-600 border-red-200" : "bg-green-50 text-green-600 border-green-200"}`}
        >
          {violationCount} Issues
        </span>
        {criticalCount > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-red-100 text-red-700 border-red-200">
            {criticalCount} Critical
          </span>
        )}
        {seriousCount > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-orange-100 text-orange-700 border-orange-200">
            {seriousCount} Serious
          </span>
        )}
      </div>
    </button>
  );
};

const SCANS_PER_PAGE = 10;

const RollbackPreviewModal = ({ isOpen, onClose, rollbackInfo, selectedSelectors, onConfirmRollback, isRollingBack }) => {
  if (!isOpen || !rollbackInfo) return null;

  const allFixes = rollbackInfo.fixes || [];
  const fixes =
    selectedSelectors && selectedSelectors.length > 0
      ? allFixes.filter((f) => selectedSelectors.includes(f.nestedSelector))
      : allFixes;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rollback Preview" size="lg">
      <div className="relative py-2">
        {isRollingBack && (
          <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-[1px] flex flex-col items-center justify-center gap-3 rounded-lg">
            <LoadingSpinner size="lg" />
            <p className="text-sm font-medium text-gray-600">Rolling back fixes...</p>
          </div>
        )}
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700 flex items-center gap-2">
            <Icon icon="mdi:alert" className="text-lg" />
            Rolling back will restore the original HTML for the selected elements.
          </p>
        </div>
        
        {fixes.length > 0 ? (
          <div className="space-y-3 max-h-80 overflow-y-auto">
            <p className="text-xs font-semibold text-gray-500 uppercase">
              {fixes.length} fix{fixes.length !== 1 ? 'es' : ''} will be rolled back:
            </p>
            {fixes.map((fix, index) => (
              <div key={index} className="p-3 bg-white border border-gray-200 rounded-lg">
                <p className="text-xs font-medium text-gray-600 mb-1">
                  {fix.violationId} • {fix.impact} severity
                </p>
                <p className="text-[10px] text-gray-400 font-mono mb-2 truncate">
                  {fix.nestedSelector}
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] font-semibold text-red-600 mb-1">Original:</p>
                    <code className="block bg-red-50 p-2 rounded text-red-700 overflow-x-auto">
                      {fix.originalHtml}
                    </code>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-green-600 mb-1">Fixed:</p>
                    <code className="block bg-green-50 p-2 rounded text-green-700 overflow-x-auto">
                      {fix.fixedHtml}
                    </code>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-8">No fixes found to rollback.</p>
        )}

        <div className="flex gap-3 mt-6 justify-end">
          <Button variant="secondary" onClick={onClose} disabled={isRollingBack}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirmRollback} disabled={isRollingBack || fixes.length === 0}>
            {isRollingBack ? "Rolling back..." : "Confirm Rollback"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const WebsiteScanHistory = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getWebsiteScans, isGettingWebsiteScans } = useWebsite();
  const { getScanById, isGettingScanById } = useAccessibility();
  const { rollbackFixes, isRollingBack, getRollbackInfo, isGettingRollbackInfo, rollbackInfo } = useWordPressConnection();

  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [error, setError] = useState(null);
  const [listOpen, setListOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [rollbackPageUrl, setRollbackPageUrl] = useState(null);
  const [rollbackSelectors, setRollbackSelectors] = useState([]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page === 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  };

  const fetchScanDetail = (scanId) => {
    getScanById(scanId, {
      onSuccess: (response) => setSelectedScan(response.data),
      onError: () => {},
    });
  };

  useEffect(() => {
    if (!websiteId) return;
    getWebsiteScans(
      { websiteId, page: currentPage, limit: SCANS_PER_PAGE },
      {
        onSuccess: (response) => {
          const rawScans = Array.isArray(response.data) ? response.data : [];
          setScans(rawScans);
          setTotalPages(response.totalPages ?? 1);
          setTotalResults(response.totalResults ?? rawScans.length);
          if (rawScans.length > 0) fetchScanDetail(rawScans[0]._id);
        },
        onError: (err) => {
          setError(
            err.response?.data?.message || err.message || "Failed to load scans",
          );
        },
      },
    );
  }, [websiteId, currentPage]);

  const handleSelectScan = (scan) => {
    fetchScanDetail(scan._id);
  };

  const handleExportReport = () => {
    if (!selectedScan) return;
    const blob = new Blob([JSON.stringify(selectedScan, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scan-report-${selectedScan._id || Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleOpenRollbackPreview = (scan, selectedSelectors = []) => {
    if (!scan?.url) {
      toast.error("Page URL not available for rollback");
      return;
    }
    setRollbackPageUrl(scan.url);
    setRollbackSelectors(selectedSelectors);
    getRollbackInfo(
      { connectionId: websiteId, pageUrl: scan.url },
      {
        onSuccess: (response) => {
          if (response.success || response.data) {
            setShowRollbackModal(true);
          } else {
            toast.error("No applied fixes found to rollback");
          }
        },
        onError: (error) => {
          if (error.response?.status === 404) {
            toast.info("No applied fixes found for this page yet.");
          } else {
            toast.error(
              "Error fetching rollback info: " +
              (error.response?.data?.message || error.message),
            );
          }
        },
      },
    );
  };

const handleConfirmRollback = () => {
    rollbackFixes(
      { connectionId: websiteId, pageUrl: rollbackPageUrl, nestedSelectors: rollbackSelectors },
      {
        onSuccess: (response) => {
          if (response.success) {
            // Refetch the scan to show updated state after rollback
            if (selectedScan?._id) {
              getScanById(selectedScan._id, {
                onSuccess: (res) => {
                  setSelectedScan(res.data);
                },
              });
            }
            toast.success("Fixes rolled back successfully!");
          } else {
            toast.error("Failed to rollback fixes");
          }
          setShowRollbackModal(false);
          setRollbackPageUrl(null);
          setRollbackSelectors([]);
        },
        onError: (error) => {
          toast.error(
            "Error rolling back fixes: " +
            (error.response?.data?.message || error.message),
          );
        },
      },
    );
  };

  const handleRollbackModalClose = () => {
    setShowRollbackModal(false);
    setRollbackPageUrl(null);
    setRollbackSelectors([]);
  };

  return (
    <PageContainer>
      {/* Header row: back button + title/subtitle */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 md:mb-6 justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-text-dark mb-0.5">
            Scan History
          </h1>
          <p className="text-sm md:text-base text-text-secondary">
            All accessibility scans for this website connection
          </p>
        </div>
        <button
          onClick={() => navigate("/integrations")}
          className="flex items-center gap-1.5 shrink-0 text-sm text-white bg-primary px-3 py-2 rounded-lg hover:opacity-90 transition-opacity"
        >
          <Icon icon="mdi:arrow-left" />
          Back to Integrations
        </button>
      </div>

      {/* Loading */}
      {isGettingWebsiteScans && (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
          <span className="ml-3 text-gray-500">Loading scan history...</span>
        </div>
      )}

      {/* Error */}
      {error && !isGettingWebsiteScans && (
        <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-6">
          <Icon icon="mdi:alert-circle" className="text-xl shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Empty */}
      {!isGettingWebsiteScans && !error && scans.length === 0 && (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <Icon
            icon="mdi:history"
            className="text-5xl text-gray-300 mx-auto mb-4"
          />
          <h3 className="text-base font-semibold text-gray-700 mb-1">
            No scan records
          </h3>
          <p className="text-sm text-gray-400">
            No accessibility scans have been performed yet.
          </p>
        </div>
      )}

      {/* Main Layout */}
      {!isGettingWebsiteScans && scans.length > 0 && (
        <div className="flex flex-col lg:flex-row lg:gap-5 lg:items-start">
          {/* Scan List — full width on mobile, sidebar on desktop */}
          <div className="w-full lg:w-72 mb-6 lg:mb-0">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* List Header — collapsible toggle on mobile */}
              <button
                onClick={() => setListOpen((prev) => !prev)}
                className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-pointer"
              >
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Icon icon="mdi:format-list-bulleted" />
                  All Scans
                  <span className="text-xs font-bold bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {totalResults}
                  </span>
                </h3>
                <Icon
                  icon={listOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
                  className="text-gray-400 text-xl lg:hidden"
                />
              </button>

              {/* List Items */}
              {listOpen && (
                <div className="p-2 space-y-2 max-h-64 lg:max-h-full overflow-y-auto">
                  {scans.map((scan) => (
                    <ScanListItem
                      key={scan._id}
                      scan={scan}
                      isSelected={selectedScan?._id === scan._id}
                      onSelect={handleSelectScan}
                    />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-3 py-2 border-t border-gray-200 flex items-center justify-between gap-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isGettingWebsiteScans}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon icon="mdi:chevron-left" />
                    Prev
                  </button>
                  <span className="text-xs text-gray-500">
                    Page {currentPage} of {totalPages}
                    <span className="text-gray-400 ml-1">({totalResults} total)</span>
                  </span>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || isGettingWebsiteScans}
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                    <Icon icon="mdi:chevron-right" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Selected Scan Detail */}
          <div className="flex-1 min-w-0">
            {isGettingScanById ? (
              <div className="flex items-center justify-center py-20">
                <LoadingSpinner />
                <span className="ml-3 text-gray-500">Loading scan details...</span>
              </div>
            ) : selectedScan ? (
              <ScanResults
                scan={selectedScan}
                onExportReport={handleExportReport}
                onRollbackFixes={(selectedSelectors) =>
                  handleOpenRollbackPreview(selectedScan, selectedSelectors)
                }
                isRollingBack={isRollingBack}
                isGettingRollbackInfo={isGettingRollbackInfo}
              />
            ) : null}
          </div>
        </div>
      )}

      <RollbackPreviewModal
        isOpen={showRollbackModal}
        onClose={handleRollbackModalClose}
        rollbackInfo={rollbackInfo?.data || rollbackInfo}
        selectedSelectors={rollbackSelectors}
        onConfirmRollback={handleConfirmRollback}
        isRollingBack={isRollingBack}
      />
    </PageContainer>
  );
};

export default WebsiteScanHistory;
